/* irio-mix.js
 * Driver for <irio-buy-box-mix> — polished PDP with flavor-mix mechanics:
 *  · gallery (thumbs + main image)
 *  · tabs
 *  · pack cards (1 / 2 / 3 doypacks) driven by section blocks
 *  · N flavor <select>s per selected pack (hidden if product has 1 flavor)
 *  · subscription toggle
 *  · /cart/add.js multi-line submit + cart drawer refresh
 */

(function () {
  'use strict';

  function boot() {
    document.querySelectorAll('.irio-mix').forEach(initRoot);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /** Re-run init on `.irio-mix` descendants of `container` (e.g. Quick View HTML injected after DOMContentLoaded). */
  function refresh(container) {
    if (!container || typeof container.querySelectorAll !== 'function') return;
    container.querySelectorAll('.irio-mix').forEach(function (root) {
      root.__irioMixInit = false;
      initRoot(root);
    });
  }

  window.IrioMix = { refresh: refresh };

  function initRoot(root) {
    if (root.__irioMixInit) return;
    root.__irioMixInit = true;

    var cfg = readConfig(root);
    if (!cfg) return;

    var variants = cfg.variants || [];
    var images   = cfg.images || [];

    if (!variants.length) {
      console.warn('[irio-mix] Product has no variants.');
      return;
    }

    /* Remote theme JSON can omit pack blocks while Liquid still renders SSR cards. */
    var wrapPacksEl = root.querySelector('[data-irio-mix-packs]');
    if (!(cfg.packs || []).length && wrapPacksEl) {
      var hydrated = parsePacksFromDom(wrapPacksEl);
      if (hydrated.length) cfg.packs = hydrated;
    }

    var packs = cfg.packs || [];
    if (!packs.length) {
      console.warn('[irio-mix] No Pack blocks. Push templates (product.energy.json, product.sleep.json, product.night.json) or add Pack blocks in the theme editor.');
      return;
    }

    var isSingleFlavor = variants.length <= 1;
    if (isSingleFlavor) root.setAttribute('data-single-flavor', 'true');

    var state = {
      selectedPack: packs[0],
      flavors: [] // array of variant IDs selected per doypack slot
    };

    initGallery(root, images);
    initTabs(root);
    initPackCards(root, cfg, state, function () {
      renderFlavors(root, cfg, state);
      updateCta(root, state);
    });
    renderFlavors(root, cfg, state);
    initSubscription(root);
    initSubmit(root, cfg, state);

    updateCta(root, state);
  }

  /* ── Config ─────────────────────────────────────────────── */

  function readConfig(root) {
    var node = root.querySelector('[data-irio-mix-data]');
    if (!node) return null;
    try { return JSON.parse(node.textContent); }
    catch (e) { console.error('[irio-mix] invalid config JSON', e); return null; }
  }

  /* ── Gallery ────────────────────────────────────────────── */

    function initGallery(root, images) {
    var main = root.querySelector('[data-irio-mix-main]');
    var wrap = root.querySelector('[data-irio-mix-thumbs]');
    var mainWrap = root.querySelector('.irio-mix__main-wrap');
    if (!main || !wrap || !images.length) return;

    var currentIdx = 0;

    function goTo(idx) {
      if (idx < 0) idx = images.length - 1;
      if (idx >= images.length) idx = 0;
      currentIdx = idx;
      var img = images[idx];
      main.classList.add('is-loading');
      var next = new Image();
      next.src = img.src;
      next.onload = function () {
        main.src = img.src;
        main.alt = img.alt || '';
        main.classList.remove('is-loading');
      };
      next.onerror = function () { main.classList.remove('is-loading'); };
      wrap.querySelectorAll('.irio-mix__thumb').forEach(function (b, i) {
        b.classList.toggle('is-active', i === idx);
      });
    }

    images.forEach(function (img, idx) {
      var btn = document.createElement('button');
      btn.className = 'irio-mix__thumb' + (idx === 0 ? ' is-active' : '');
      btn.type = 'button';
      btn.setAttribute('aria-label', 'Imagen ' + (idx + 1));
      var el = document.createElement('img');
      el.src = img.thumb;
      el.alt = img.alt || '';
      el.loading = idx === 0 ? 'eager' : 'lazy';
      btn.appendChild(el);
      btn.addEventListener('click', function () { goTo(idx); });
      wrap.appendChild(btn);
    });

    // Flechas prev/next
    if (mainWrap && images.length > 1) {
      var prev = document.createElement('button');
      prev.className = 'irio-mix__nav irio-mix__nav--prev';
      prev.type = 'button';
      prev.setAttribute('aria-label', 'Imagen anterior');
      prev.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>';
      prev.addEventListener('click', function () { goTo(currentIdx - 1); });

      var nxt = document.createElement('button');
      nxt.className = 'irio-mix__nav irio-mix__nav--next';
      nxt.type = 'button';
      nxt.setAttribute('aria-label', 'Imagen siguiente');
      nxt.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>';
      nxt.addEventListener('click', function () { goTo(currentIdx + 1); });

      mainWrap.appendChild(prev);
      mainWrap.appendChild(nxt);

      // Swipe gestures
      var startX = 0;
      mainWrap.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
      mainWrap.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) goTo(currentIdx + (dx < 0 ? 1 : -1));
      }, { passive: true });
    }
  }
  /* ── Tabs ───────────────────────────────────────────────── */

  function initTabs(root) {
    var btns   = root.querySelectorAll('.irio-mix__tab-btn');
    var panels = root.querySelectorAll('.irio-mix__tab-panel');
    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = btn.dataset.tab;
        btns.forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-selected', 'false');
        });
        panels.forEach(function (p) { p.classList.remove('is-active'); });
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        var panel = root.querySelector('.irio-mix__tab-panel[data-panel="' + idx + '"]');
        if (panel) panel.classList.add('is-active');
      });
    });
  }

  /* ── Pack cards ─────────────────────────────────────────── */

  function parsePacksFromDom(wrap) {
    var result = [];
    if (!wrap) return result;
    wrap.querySelectorAll('.irio-mix__option').forEach(function (el) {
      var key = el.getAttribute('data-pack-key') || '';
      var dp = parseInt(el.getAttribute('data-doypacks') || '1', 10) || 1;
      var nm = el.querySelector('.irio-mix__option-name');
      result.push({
        key: key,
        doypacks: dp,
        label: nm ? nm.textContent.trim() : '',
        desc: '',
        badge: '',
        badgeStyle: 'filled',
        saveLabel: '',
        savePercent: parseFloat(el.getAttribute('data-save-percent') || '0') || 0,
        isFullWidth: el.classList.contains('is-full-width'),
        unitPrice: parseInt(el.getAttribute('data-unit-price') || '0', 10) || 0,
        unitCompare: parseInt(el.getAttribute('data-unit-compare') || '0', 10) || 0
      });
    });
    return result;
  }

  function initPackCards(root, cfg, state, onChange) {
    var wrap = root.querySelector('[data-irio-mix-packs]');
    if (!wrap) return;

    if (!cfg.packs || !cfg.packs.length) {
      cfg.packs = parsePacksFromDom(wrap);
    }
    if (!cfg.packs || !cfg.packs.length) return;

    var sid = String(cfg.sectionId != null ? cfg.sectionId : '');
    wrap.innerHTML = cfg.packs.map(function (pack, idx) {
      var isSel = idx === 0;
      return renderPackCard(pack, isSel, cfg, sid);
    }).join('');

    wrap.querySelectorAll('.irio-mix__option').forEach(function (card) {
      card.addEventListener('click', function (e) {
        if (e.target.matches('input[type="radio"]')) return;
        selectPack(wrap, card, cfg, state, onChange);
      });
    });

    wrap.querySelectorAll('.irio-mix__radio').forEach(function (r) {
      r.addEventListener('change', function () {
        if (!r.checked) return;
        var card = r.closest('.irio-mix__option');
        if (card) selectPack(wrap, card, cfg, state, onChange);
      });
    });
  }

  function selectPack(wrap, card, cfg, state, onChange) {
    wrap.querySelectorAll('.irio-mix__option').forEach(function (c) { c.classList.remove('is-selected'); });
    card.classList.add('is-selected');
    var radio = card.querySelector('.irio-mix__radio');
    if (radio) radio.checked = true;
    var key = card.dataset.packKey;
    state.selectedPack = cfg.packs.find(function (p) { return String(p.key) === String(key); }) || cfg.packs[0];
    if (typeof onChange === 'function') onChange();
  }

  function renderPackCard(pack, isSel, cfg, sectionId) {
    var doypacks    = Number(pack.doypacks) || 1;
    var unitPrice   = Number(pack.unitPrice)   || 0;
    var unitCompare = Number(pack.unitCompare) || unitPrice;
    var savePct     = Number(pack.savePercent) || 0;
    if (savePct < 0) savePct = 0;
    if (savePct > 100) savePct = 100;

    var grossCents   = unitPrice * doypacks;
    var compareGross = unitCompare * doypacks;

    var nowCents, beforeCents;
    if (savePct > 0) {
      nowCents    = Math.round(grossCents * (100 - savePct) / 100);
      beforeCents = compareGross;
    } else {
      nowCents    = grossCents;
      beforeCents = compareGross;
    }

    var priceHtml = '<span class="irio-mix__option-price">' + formatMoney(nowCents) + '</span>';
    if (beforeCents > nowCents) {
      priceHtml += '<span class="irio-mix__option-compare">' + formatMoney(beforeCents) + '</span>';
    }
        var totalSticks = doypacks * 14;
    var perStickCents = Math.round(nowCents / totalSticks);
    var perStickHtml = '<div class="irio-mix__option-perstick" style="font-size:12px;color:#6b7280;padding-left:35px;margin-top:2px;">' + formatMoney(perStickCents) + ' por porción</div>';

    var badgeHtml = '';
    if (pack.badge) {
      var style = pack.badgeStyle === 'text' ? ' is-text' : '';
      badgeHtml = '<span class="irio-mix__option-badge' + style + '">' + escapeHtml(pack.badge) + '</span>';
    }

    var saveHtml = pack.saveLabel
      ? '<div class="irio-mix__option-save">' + escapeHtml(pack.saveLabel) + '</div>'
      : '';

    var descHtml = pack.desc
      ? '<div class="irio-mix__option-desc">' + escapeHtml(pack.desc) + '</div>'
      : '';

    var fullWidth = pack.isFullWidth ? ' is-full-width' : '';
    var selClass  = isSel ? ' is-selected' : '';
    var radioName = 'irio-mix-pack-' + (sectionId || '');

    return ''
      + '<label class="irio-mix__option' + selClass + fullWidth + '" data-pack-key="' + escapeAttr(pack.key) + '" data-doypacks="' + escapeAttr(String(doypacks)) + '" data-unit-price="' + escapeAttr(String(unitPrice)) + '" data-unit-compare="' + escapeAttr(String(unitCompare)) + '" data-save-percent="' + escapeAttr(String(savePct)) + '">'
      +   '<input type="radio" class="irio-mix__radio" name="' + escapeAttr(radioName) + '" value="' + escapeAttr(pack.key) + '"' + (isSel ? ' checked' : '') + '>'
      +   badgeHtml
      +   '<div class="irio-mix__option-body">'
      +     '<div class="irio-mix__option-header">'
      +       '<span class="irio-mix__option-dot" aria-hidden="true"></span>'
      +       '<span class="irio-mix__option-name">' + escapeHtml(pack.label || (doypacks + ' doypack')) + '</span>'
      +     '</div>'
      +     descHtml
      +     perStickHtml
      +     '<div class="irio-mix__option-prices">' + priceHtml + '</div>'
      +     saveHtml
      +   '</div>'
      + '</label>';
  }

  /* ── Flavor dropdowns ───────────────────────────────────── */

  function renderFlavors(root, cfg, state) {
    var wrap = root.querySelector('[data-irio-mix-flavors]');
    if (!wrap) return;

    var variants = cfg.variants || [];
    if (variants.length <= 1) {
      wrap.hidden = true;
      wrap.innerHTML = '';
      return;
    }

    wrap.hidden = false;
    var n = Number(state.selectedPack && state.selectedPack.doypacks) || 1;

    // Ensure the state array has n entries; seed with first available variant
    var defaultId = pickDefault(variants);
    state.flavors = Array.from({ length: n }, function (_, i) {
      return state.flavors[i] || defaultId;
    });

    var title = '<p class="irio-mix__flavors-title">' + escapeHtml(cfg.mixHint || 'Elige un sabor para cada doypack') + '</p>';
    var items = [];
    for (var i = 0; i < n; i++) {
      items.push(buildFlavorItem(i, variants, state.flavors[i]));
    }
    wrap.innerHTML = title + '<div class="irio-mix__flavor-grid">' + items.join('') + '</div>';

    wrap.querySelectorAll('[data-irio-mix-flavor]').forEach(function (sel) {
      sel.addEventListener('change', function () {
        var idx = Number(sel.dataset.irioMixFlavor);
        state.flavors[idx] = sel.value;
      });
    });
  }

  function pickDefault(variants) {
    var v = variants.find(function (x) { return x.available; }) || variants[0];
    return v ? String(v.id) : '';
  }

  function buildFlavorItem(i, variants, selectedId) {
    var options = variants.map(function (v) {
      var sel = String(v.id) === String(selectedId) ? ' selected' : '';
      var dis = v.available ? '' : ' disabled';
      var suffix = v.available ? '' : ' (agotado)';
      return '<option value="' + escapeAttr(v.id) + '"' + sel + dis + '>' + escapeHtml((v.label || '') + suffix) + '</option>';
    }).join('');

    return ''
      + '<div class="irio-mix__flavor-item">'
      +   '<span class="irio-mix__flavor-index" aria-hidden="true">#' + (i + 1) + '</span>'
      +   '<div class="irio-mix__flavor-select-wrap">'
      +     '<select class="irio-mix__flavor-select" data-irio-mix-flavor="' + i + '" aria-label="Sabor ' + (i + 1) + '">'
      +       options
      +     '</select>'
      +     '<span class="irio-mix__flavor-arrow" aria-hidden="true">'
      +       '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 5l4 4 4-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      +     '</span>'
      +   '</div>'
      + '</div>';
  }

  /* ── Subscription toggle ────────────────────────────────── */

  function initSubscription(root) {
    var sub = root.querySelector('[data-irio-mix-sub]');
    if (!sub) return;
    sub.addEventListener('click', function () { sub.classList.toggle('is-selected'); });
  }

  /* ── CTA label / disabled state ─────────────────────────── */

  function updateCta(root, state) {
    var btn = root.querySelector('[data-irio-mix-submit]');
    if (!btn) return;
    btn.disabled = false;
  }

  /* ── Submit (add to cart) ───────────────────────────────── */

  function initSubmit(root, cfg, state) {
    var form      = root.querySelector('[data-irio-mix-form]');
    var submitBtn = root.querySelector('[data-irio-mix-submit]');
    var buyNowBtn = root.querySelector('[data-irio-mix-buy-now]');
    var errorBox  = root.querySelector('[data-irio-mix-error]');

    function showError(msg) {
      root.setAttribute('data-has-error', 'true');
      if (errorBox) errorBox.textContent = msg;
    }
    function clearError() {
      root.setAttribute('data-has-error', 'false');
      if (errorBox) errorBox.textContent = '';
    }

    function buildItems() {
      var pack = state.selectedPack;
      if (!pack) return [];
      var variants = cfg.variants || [];

      if (variants.length <= 1) {
        var only = variants[0];
        return only ? [{ id: Number(only.id), quantity: Number(pack.doypacks) || 1 }] : [];
      }

      // Aggregate per-dropdown quantities into items[]
      var tally = {};
      (state.flavors || []).forEach(function (id) {
        if (!id) return;
        tally[id] = (tally[id] || 0) + 1;
      });
      return Object.keys(tally).map(function (id) {
        return { id: Number(id), quantity: tally[id] };
      });
    }

    function submit(redirectToCheckout) {
      clearError();
      var items = buildItems();
      if (!items.length) {
        showError('Selecciona un sabor para cada doypack.');
        return;
      }

      root.setAttribute('data-loading', 'true');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items })
      })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw err; });
        return res.json();
      })
      .then(function () {
        if (redirectToCheckout) {
          window.location.href = '/checkout';
          return;
        }
        return fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
            document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
            setTimeout(function () {
              var trigger = document.querySelector('#minicart-drawer-toggle')
                         || document.querySelector('#cart-icon-bubble')
                         || document.querySelector('.header__icon--cart');
              if (trigger) trigger.click();
            }, 80);
          });
      })
      .catch(function (err) {
        showError((err && err.description) || 'No pudimos agregar al carrito. Intenta de nuevo.');
      })
      .finally(function () {
        root.setAttribute('data-loading', 'false');
      });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        submit(false);
      });
    }
    if (submitBtn) {
      submitBtn.addEventListener('click', function (e) {
        if (submitBtn.type !== 'submit') {
          e.preventDefault();
          submit(false);
        }
      });
    }
    if (buyNowBtn) {
      buyNowBtn.addEventListener('click', function (e) {
        e.preventDefault();
        submit(true);
      });
    }
  }

  /* ── Helpers ────────────────────────────────────────────── */

  function formatMoney(cents) {
    return '$' + Math.round(cents / 100).toLocaleString('es-CL');
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  function escapeAttr(str) {
    return escapeHtml(str);
  }
})();
